import { FC, useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  TextField,
  Card,
  CardContent,
  CardActions,
  Grid,
  Chip,
  Alert,
  CircularProgress,
  Tooltip,
  Tabs,
  Tab,
  Checkbox,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  RadioGroup,
  Radio,
  FormLabel,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import StorageIcon from '@mui/icons-material/Storage';
import SchoolIcon from '@mui/icons-material/School';
import CloudIcon from '@mui/icons-material/Cloud';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FolderIcon from '@mui/icons-material/Folder';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import UploadIcon from '@mui/icons-material/Upload';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import SecurityIcon from '@mui/icons-material/Security';
import BuildIcon from '@mui/icons-material/Build';
import { s3BucketsApi, S3BucketConfig, S3BucketEnvironment } from '../services/s3-buckets-api';
import { moodleApi, MoodleSource, ConnectionTestResult } from '../services/moodle-api';
import { vaultApi, VaultConfig, VaultAWSPath, VaultKVPath } from '../services/integrations-api';
import { environmentApi } from '../services/api';
import { MoodleSourceManager } from './MoodleSourceManager';
import { RangeOSEnvironmentManager } from './RangeOSEnvironmentManager';
import { RangeOSManager } from './RangeOSManager';
import { IntegrationsManager } from './IntegrationsManager';

interface SettingsDialogProps {
  open: boolean;
  onClose: () => void;
  initialTab?: number;
}

interface BucketFormData {
  name: string;
  endpoint: string;
  region: string;
  bucket: string;
  credentialSource: 'static' | 'vault' | 'environment';
  accessKeyId: string;
  secretAccessKey: string;
  sessionToken: string;
  vaultPathId: string;
  isDefault: boolean;
  autoDiscoverEnvironments: boolean;
  description: string;
}

interface EnvironmentFormData {
  bucketId: string;
  name: string;
  prefix: string;
  description: string;
}

const emptyBucketForm: BucketFormData = {
  name: '',
  endpoint: '', // Empty means use AWS default
  region: 'us-east-1',
  bucket: '',
  credentialSource: 'static',
  accessKeyId: '',
  secretAccessKey: '',
  sessionToken: '',
  vaultPathId: '',
  isDefault: false,
  autoDiscoverEnvironments: true,
  description: '',
};

const emptyEnvForm: EnvironmentFormData = {
  bucketId: '',
  name: '',
  prefix: '',
  description: '',
};

export const SettingsDialog: FC<SettingsDialogProps> = ({ open, onClose, initialTab = 0 }) => {
  const [activeTab, setActiveTab] = useState(initialTab);

  // Update activeTab when initialTab changes
  useEffect(() => {
    if (open && initialTab !== undefined) {
      setActiveTab(initialTab);
    }
  }, [open, initialTab]);
  const [buckets, setBuckets] = useState<S3BucketConfig[]>([]);
  const [environments, setEnvironments] = useState<S3BucketEnvironment[]>([]);
  const [moodleSources, setMoodleSources] = useState<MoodleSource[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // S3 Bucket state
  const [showAddBucket, setShowAddBucket] = useState(false);
  const [editingBucket, setEditingBucket] = useState<S3BucketConfig | null>(null);

  // Export/Import state
  const [exportFormat, setExportFormat] = useState<'yaml' | 'json'>('yaml');
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importOptions, setImportOptions] = useState({
    overwrite: false,
    skipS3: false,
    skipMoodle: false,
    skipRangeOS: false,
  });
  const [importResult, setImportResult] = useState<any>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [bucketForm, setBucketForm] = useState<BucketFormData>(emptyBucketForm);
  const [vaultConfig, setVaultConfig] = useState<VaultConfig | null>(null);
  const [vaultPaths, setVaultPaths] = useState<VaultAWSPath[]>([]);
  const [vaultKVPaths, setVaultKVPaths] = useState<VaultKVPath[]>([]);

  // Environment state
  const [showAddEnv, setShowAddEnv] = useState(false);
  const [editingEnv, setEditingEnv] = useState<S3BucketEnvironment | null>(null);
  const [envForm, setEnvForm] = useState<EnvironmentFormData>(emptyEnvForm);
  const [selectedBucketForNewEnv, setSelectedBucketForNewEnv] = useState<string | null>(null);

  // Moodle Sources state
  const [testingConnection, setTestingConnection] = useState<string | null>(null);
  const [connectionResults, setConnectionResults] = useState<Map<string, ConnectionTestResult>>(new Map());
  const [moodleManagerOpen, setMoodleManagerOpen] = useState(false);
  const [editingMoodleSourceId, setEditingMoodleSourceId] = useState<string | undefined>(undefined);

  // Delete confirmation dialog state
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean;
    type: 'bucket' | 'environment';
    id: string;
    name: string;
    deleteContent: boolean;
  } | null>(null);

  // CMI5 Index rebuild state
  const [rebuildingIndex, setRebuildingIndex] = useState<string | null>(null);
  const [rebuildResults, setRebuildResults] = useState<Map<string, { success: boolean; message: string }>>(new Map());

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      if (activeTab === 0) {
        // Load S3 buckets and environments
        const [bucketsData, environmentsData] = await Promise.all([
          s3BucketsApi.listBuckets(),
          s3BucketsApi.listEnvironments(),
        ]);
        setBuckets(bucketsData);
        setEnvironments(environmentsData);

        // Load Vault configuration if available
        try {
          const vaultConf = await vaultApi.getConfig();
          setVaultConfig(vaultConf);
          if (vaultConf?.awsSecretsEngine?.paths) {
            setVaultPaths(vaultConf.awsSecretsEngine.paths);
          }
          if (vaultConf?.kvPaths) {
            setVaultKVPaths(vaultConf.kvPaths);
          }
        } catch (vaultErr) {
          // Vault not configured is okay
          console.log('Vault not configured');
        }
      } else if (activeTab === 1) {
        // Load Moodle sources
        const sourcesData = await moodleApi.listSources();
        setMoodleSources(sourcesData);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load configuration');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      loadData();
    }
  }, [open, activeTab]);

  // ============================================================================
  // Bucket Management
  // ============================================================================

  const handleAddBucket = async () => {
    try {
      await s3BucketsApi.addBucket({
        name: bucketForm.name,
        endpoint: bucketForm.endpoint,
        region: bucketForm.region,
        bucket: bucketForm.bucket,
        credentialSource: bucketForm.credentialSource,
        accessKeyId: bucketForm.accessKeyId || undefined,
        secretAccessKey: bucketForm.secretAccessKey || undefined,
        sessionToken: bucketForm.sessionToken || undefined,
        vaultPathId: bucketForm.vaultPathId || undefined,
        isDefault: bucketForm.isDefault,
        autoDiscoverEnvironments: bucketForm.autoDiscoverEnvironments,
        description: bucketForm.description || undefined,
      });
      await loadData();
      setShowAddBucket(false);
      setBucketForm(emptyBucketForm);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add bucket');
    }
  };

  const handleUpdateBucket = async () => {
    if (!editingBucket) return;
    try {
      const updates: Partial<Omit<S3BucketConfig, 'id' | 'createdAt'>> = {
        name: bucketForm.name,
        endpoint: bucketForm.endpoint,
        region: bucketForm.region,
        bucket: bucketForm.bucket,
        credentialSource: bucketForm.credentialSource,
        isDefault: bucketForm.isDefault,
        autoDiscoverEnvironments: bucketForm.autoDiscoverEnvironments,
        description: bucketForm.description || undefined,
      };

      // Include credential fields based on source
      if (bucketForm.credentialSource === 'static') {
        if (bucketForm.accessKeyId) updates.accessKeyId = bucketForm.accessKeyId;
        if (bucketForm.secretAccessKey) updates.secretAccessKey = bucketForm.secretAccessKey;
        if (bucketForm.sessionToken) updates.sessionToken = bucketForm.sessionToken;
      } else if (bucketForm.credentialSource === 'vault') {
        updates.vaultPathId = bucketForm.vaultPathId;
      }

      await s3BucketsApi.updateBucket(editingBucket.id, updates);
      await loadData();
      setEditingBucket(null);
      setBucketForm(emptyBucketForm);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update bucket');
    }
  };

  const handleDeleteBucket = async (id: string) => {
    const bucket = buckets.find(b => b.id === id);
    const bucketName = bucket?.name || 'this bucket';
    
    // Open confirmation dialog
    setDeleteDialog({
      open: true,
      type: 'bucket',
      id,
      name: bucketName,
      deleteContent: false, // Default to NOT deleting content
    });
  };

  const handleDeleteEnvironment = async (id: string) => {
    const env = environments.find(e => e.id === id);
    const envName = env?.name || 'this environment';
    
    // Open confirmation dialog
    setDeleteDialog({
      open: true,
      type: 'environment',
      id,
      name: envName,
      deleteContent: false, // Default to NOT deleting content
    });
  };

  const handleConfirmDelete = async () => {
    if (!deleteDialog) return;

    try {
      if (deleteDialog.type === 'bucket') {
        await s3BucketsApi.removeBucket(deleteDialog.id, deleteDialog.deleteContent);
      } else {
        await s3BucketsApi.removeEnvironment(deleteDialog.id, deleteDialog.deleteContent);
      }
      await loadData();
      setDeleteDialog(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : `Failed to delete ${deleteDialog.type}`);
      setDeleteDialog(null);
    }
  };

  const handleEditBucket = (bucket: S3BucketConfig) => {
    setEditingBucket(bucket);
    setBucketForm({
      name: bucket.name,
      endpoint: bucket.endpoint,
      region: bucket.region,
      bucket: bucket.bucket,
      credentialSource: bucket.credentialSource || 'static',
      accessKeyId: '',
      secretAccessKey: '',
      sessionToken: '',
      vaultPathId: bucket.vaultPathId || '',
      isDefault: bucket.isDefault || false,
      autoDiscoverEnvironments: bucket.autoDiscoverEnvironments !== false,
      description: bucket.description || '',
    });
  };

  const isBucketFormValid = (): boolean => {
    return !!(
      bucketForm.name.trim() &&
      bucketForm.region.trim() &&
      bucketForm.bucket.trim()
      // endpoint is optional - empty means use AWS default
    );
  };

  // ============================================================================
  // Environment Management
  // ============================================================================

  const handleAddEnvironment = async () => {
    try {
      await s3BucketsApi.addEnvironment({
        bucketId: envForm.bucketId,
        name: envForm.name,
        prefix: envForm.prefix,
        description: envForm.description || undefined,
      });
      await loadData();
      setShowAddEnv(false);
      setEnvForm(emptyEnvForm);
      setSelectedBucketForNewEnv(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add environment');
    }
  };

  const handleUpdateEnvironment = async () => {
    if (!editingEnv) return;
    try {
      await s3BucketsApi.updateEnvironment(editingEnv.id, {
        bucketId: envForm.bucketId,
        name: envForm.name,
        prefix: envForm.prefix,
        description: envForm.description || undefined,
      });
      await loadData();
      setEditingEnv(null);
      setEnvForm(emptyEnvForm);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update environment');
    }
  };

  const handleEditEnvironment = (env: S3BucketEnvironment) => {
    setEditingEnv(env);
    setEnvForm({
      bucketId: env.bucketId,
      name: env.name,
      prefix: env.prefix,
      description: env.description || '',
    });
  };

  const isEnvFormValid = (): boolean => {
    return !!(
      envForm.bucketId &&
      envForm.name.trim() &&
      envForm.prefix.trim()
    );
  };

  const getEnvironmentsForBucket = (bucketId: string): S3BucketEnvironment[] => {
    return environments.filter(e => e.bucketId === bucketId);
  };

  const handleRebuildCMI5Index = async (envId: string) => {
    setRebuildingIndex(envId);
    setRebuildResults(new Map(rebuildResults));
    try {
      const result = await environmentApi.rebuildCMI5Index(envId);
      setRebuildResults(new Map(rebuildResults.set(envId, result)));
    } catch (err) {
      setRebuildResults(
        new Map(
          rebuildResults.set(envId, {
            success: false,
            message: err instanceof Error ? err.message : 'Failed to rebuild CMI5 index',
          })
        )
      );
    } finally {
      setRebuildingIndex(null);
    }
  };

  // ============================================================================
  // Export/Import Handlers
  // ============================================================================

  const handleExportConfig = async () => {
    setIsExporting(true);
    setError(null);
    try {
      const apiUrl = import.meta.env.VITE_API_URL || '/api';
      const response = await fetch(`${apiUrl}/config/export?format=${exportFormat}`, {
        method: 'GET',
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to export configuration');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `s3-content-manager-config-${new Date().toISOString().split('T')[0]}.${exportFormat}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      // Show success message
      setImportResult({
        success: true,
        message: 'Configuration exported successfully',
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to export configuration');
    } finally {
      setIsExporting(false);
    }
  };

  const handleImportConfig = async () => {
    if (!importFile) {
      setError('Please select a file to import');
      return;
    }

    setIsImporting(true);
    setError(null);
    setImportResult(null);

    try {
      const apiUrl = import.meta.env.VITE_API_URL || '/api';
      const formData = new FormData();
      formData.append('config', importFile);

      const queryParams = new URLSearchParams();
      if (importOptions.overwrite) queryParams.append('overwrite', 'true');
      if (importOptions.skipS3) queryParams.append('skipS3', 'true');
      if (importOptions.skipMoodle) queryParams.append('skipMoodle', 'true');
      if (importOptions.skipRangeOS) queryParams.append('skipRangeOS', 'true');

      const response = await fetch(`${apiUrl}/config/import?${queryParams.toString()}`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });

      const result = await response.json();
      setImportResult(result);

      if (result.success) {
        // Reload data to reflect imported configuration
        await loadData();
        setImportFile(null);
      } else {
        setError(result.message || 'Import failed');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to import configuration');
      setImportResult({
        success: false,
        message: err instanceof Error ? err.message : 'Failed to import configuration',
      });
    } finally {
      setIsImporting(false);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setImportFile(file);
      setImportResult(null);
      setError(null);
    }
  };

  // ============================================================================
  // Render
  // ============================================================================

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth scroll="paper">
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">Settings</Typography>
          <IconButton edge="end" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper', position: 'sticky', top: 0, zIndex: 10 }}>
        <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)} sx={{ px: 3 }}>
          <Tab icon={<StorageIcon />} label="S3 Buckets & Environments" />
          <Tab icon={<SchoolIcon />} label="Moodle Sources" />
          <Tab icon={<CloudIcon />} label="RangeOS Environments" />
          <Tab icon={<SecurityIcon />} label="Integrations" />
          <Tab icon={<ImportExportIcon />} label="Export / Import" />
        </Tabs>
      </Box>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* ============================================================ */}
            {/* Tab 0: S3 Buckets & Environments */}
            {/* ============================================================ */}
            {activeTab === 0 && (
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Configure S3 buckets and their environments. Each environment represents a prefix/folder within a bucket.
                </Typography>

                {/* Add Bucket Button */}
                {!showAddBucket && !editingBucket && (
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => setShowAddBucket(true)}
                    sx={{ mb: 2, mt: 2 }}
                  >
                    Add S3 Bucket
                  </Button>
                )}

                {/* Bucket Form */}
                {(showAddBucket || editingBucket) && (
                  <Card sx={{ mb: 3, mt: 2, bgcolor: 'action.hover' }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        {editingBucket ? 'Edit Bucket' : 'Add New Bucket'}
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Display Name"
                            value={bucketForm.name}
                            onChange={(e) => setBucketForm({ ...bucketForm, name: e.target.value })}
                            placeholder="e.g., Primary S3, Backup S3"
                            required
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Bucket Name"
                            value={bucketForm.bucket}
                            onChange={(e) => setBucketForm({ ...bucketForm, bucket: e.target.value })}
                            placeholder="my-s3-bucket"
                            required
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Endpoint URL"
                            value={bucketForm.endpoint}
                            onChange={(e) => setBucketForm({ ...bucketForm, endpoint: e.target.value })}
                            placeholder="Leave empty for AWS S3 (s3.amazonaws.com)"
                            helperText="For custom S3-compatible services, enter full URL (e.g., https://s3.example.com)"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Region"
                            value={bucketForm.region}
                            onChange={(e) => setBucketForm({ ...bucketForm, region: e.target.value })}
                            placeholder="us-east-1"
                            required
                          />
                        </Grid>

                        {/* Credential Source Selector */}
                        <Grid item xs={12}>
                          {editingBucket && (
                            <Alert severity="info" sx={{ mb: 2 }}>
                              <strong>Current Credential Source:</strong> {
                                editingBucket.credentialSource === 'vault' ? 'Vault' :
                                editingBucket.credentialSource === 'environment' ? 'Environment Variables' :
                                'Static Credentials'
                              }
                            </Alert>
                          )}
                          <FormControl component="fieldset">
                            <FormLabel component="legend">Credential Source</FormLabel>
                            <RadioGroup
                              row
                              value={bucketForm.credentialSource}
                              onChange={(e) => setBucketForm({ ...bucketForm, credentialSource: e.target.value as any })}
                            >
                              <FormControlLabel value="static" control={<Radio />} label="Static Credentials" />
                              <FormControlLabel
                                value="vault"
                                control={<Radio />}
                                label="Vault"
                                disabled={!vaultConfig || (vaultPaths.length === 0 && vaultKVPaths.length === 0)}
                              />
                              <FormControlLabel value="environment" control={<Radio />} label="Environment Variables" />
                            </RadioGroup>
                          </FormControl>
                          {bucketForm.credentialSource === 'vault' && !vaultConfig && (
                            <Alert severity="warning" sx={{ mt: 1 }}>
                              Vault is not configured. Please configure Vault in the Integrations settings first.
                            </Alert>
                          )}
                        </Grid>

                        {/* Static Credentials */}
                        {bucketForm.credentialSource === 'static' && (
                          <>
                            <Grid item xs={12} sm={6}>
                              <TextField
                                fullWidth
                                label="Access Key ID"
                                value={bucketForm.accessKeyId}
                                onChange={(e) => setBucketForm({ ...bucketForm, accessKeyId: e.target.value })}
                                placeholder={editingBucket ? '(leave blank to keep existing)' : 'Required for static credentials'}
                                type="password"
                                required={!editingBucket}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <TextField
                                fullWidth
                                label="Secret Access Key"
                                value={bucketForm.secretAccessKey}
                                onChange={(e) => setBucketForm({ ...bucketForm, secretAccessKey: e.target.value })}
                                placeholder={editingBucket ? '(leave blank to keep existing)' : 'Required for static credentials'}
                                type="password"
                                required={!editingBucket}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <TextField
                                fullWidth
                                label="Session Token (Optional)"
                                value={bucketForm.sessionToken}
                                onChange={(e) => setBucketForm({ ...bucketForm, sessionToken: e.target.value })}
                                placeholder="Optional for temporary credentials"
                                type="password"
                              />
                            </Grid>
                          </>
                        )}

                        {/* Vault Credentials */}
                        {bucketForm.credentialSource === 'vault' && (
                          <Grid item xs={12} sm={6}>
                            <FormControl fullWidth required>
                              <InputLabel>Vault Secret Path</InputLabel>
                              <Select
                                value={bucketForm.vaultPathId}
                                label="Vault Secret Path"
                                onChange={(e) => setBucketForm({ ...bucketForm, vaultPathId: e.target.value })}
                              >
                                {/* KV Secrets */}
                                {vaultKVPaths.filter(p => p.enabled).map((path) => (
                                  <MenuItem key={path.id} value={path.id}>
                                    {path.name} (KV v{path.version || 2})
                                  </MenuItem>
                                ))}
                                {/* AWS Secrets Engine */}
                                {vaultPaths.filter(p => p.enabled).map((path) => (
                                  <MenuItem key={path.id} value={path.id}>
                                    {path.name} ({path.type === 'sts' ? 'STS' : 'IAM'})
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                        )}

                        {/* Environment Credentials Info */}
                        {bucketForm.credentialSource === 'environment' && (
                          <Grid item xs={12}>
                            <Alert severity="info">
                              This bucket will use AWS credentials from environment variables:
                              <br />• AWS_ACCESS_KEY_ID
                              <br />• AWS_SECRET_ACCESS_KEY
                              <br />• AWS_SESSION_TOKEN (optional)
                            </Alert>
                          </Grid>
                        )}
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Description"
                            value={bucketForm.description}
                            onChange={(e) => setBucketForm({ ...bucketForm, description: e.target.value })}
                            placeholder="Optional description"
                            multiline
                            rows={2}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={bucketForm.isDefault}
                                onChange={(e) => setBucketForm({ ...bucketForm, isDefault: e.target.checked })}
                              />
                            }
                            label="Set as default bucket (environments will use this if not specified)"
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={bucketForm.autoDiscoverEnvironments}
                                onChange={(e) => setBucketForm({ ...bucketForm, autoDiscoverEnvironments: e.target.checked })}
                              />
                            }
                            label={
                              <Box>
                                <Typography variant="body2">Auto-discover environments from bucket folders</Typography>
                                <Typography variant="caption" color="text.secondary">
                                  When enabled, top-level folders in the bucket will automatically become environments
                                </Typography>
                              </Box>
                            }
                          />
                        </Grid>
                      </Grid>
                    </CardContent>
                    <CardActions>
                      <Button
                        onClick={() => {
                          setShowAddBucket(false);
                          setEditingBucket(null);
                          setBucketForm(emptyBucketForm);
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="contained"
                        onClick={editingBucket ? handleUpdateBucket : handleAddBucket}
                        disabled={!isBucketFormValid()}
                      >
                        {editingBucket ? 'Update' : 'Add'}
                      </Button>
                    </CardActions>
                  </Card>
                )}

                {/* Bucket List with Environments */}
                {buckets.length === 0 ? (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    No S3 buckets configured yet. Add one to get started.
                  </Alert>
                ) : (
                  <Box sx={{ mt: 2 }}>
                    {buckets.map((bucket) => {
                      const bucketEnvs = getEnvironmentsForBucket(bucket.id);
                      return (
                        <Accordion key={bucket.id} defaultExpanded>
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Box display="flex" alignItems="center" gap={1} flexGrow={1}>
                              <StorageIcon color="primary" />
                              <Typography variant="h6">{bucket.name}</Typography>
                              {bucket.isDefault && (
                                <Chip label="Default" size="small" color="primary" />
                              )}
                              {bucket.credentialSource === 'vault' ? (
                                <Chip label="Vault" size="small" color="primary" />
                              ) : bucket.credentialSource === 'environment' ? (
                                <Chip label="Environment" size="small" color="secondary" />
                              ) : (
                                <Chip label="Static" size="small" />
                              )}
                              <Box flexGrow={1} />
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditBucket(bucket);
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteBucket(bucket.id);
                                }}
                                color="error"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </AccordionSummary>
                          <AccordionDetails>
                            <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                              Endpoint: {bucket.endpoint || 'AWS S3 (s3.amazonaws.com)'} | Bucket: {bucket.bucket} | Region: {bucket.region}
                            </Typography>
                            {bucket.description && (
                              <Typography variant="body2" color="text.secondary" gutterBottom>
                                {bucket.description}
                              </Typography>
                            )}

                            <Box mt={2}>
                              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                <Typography variant="subtitle2">Environments</Typography>
                                <Button
                                  size="small"
                                  startIcon={<AddIcon />}
                                  onClick={() => {
                                    setSelectedBucketForNewEnv(bucket.id);
                                    setEnvForm({ ...emptyEnvForm, bucketId: bucket.id });
                                    setShowAddEnv(true);
                                  }}
                                >
                                  Add Environment
                                </Button>
                              </Box>

                              {bucketEnvs.length === 0 ? (
                                <Alert severity="info" sx={{ mt: 1 }}>
                                  No environments in this bucket. Add one to get started.
                                </Alert>
                              ) : (
                                <List dense>
                                  {bucketEnvs.map((env) => {
                                    const rebuildResult = rebuildResults.get(env.id);
                                    const isRebuilding = rebuildingIndex === env.id;

                                    return (
                                      <Box key={env.id}>
                                        <ListItem divider>
                                          <FolderIcon sx={{ mr: 2, color: 'text.secondary' }} />
                                          <ListItemText
                                            primary={env.name}
                                            secondary={`Prefix: ${env.prefix}${env.description ? ` - ${env.description}` : ''}`}
                                          />
                                          <ListItemSecondaryAction>
                                            <Tooltip title="Rebuild CMI5 Activity Index">
                                              <IconButton
                                                edge="end"
                                                size="small"
                                                onClick={() => handleRebuildCMI5Index(env.id)}
                                                disabled={isRebuilding}
                                                sx={{ mr: 1 }}
                                              >
                                                {isRebuilding ? <CircularProgress size={16} /> : <BuildIcon fontSize="small" />}
                                              </IconButton>
                                            </Tooltip>
                                            <IconButton
                                              edge="end"
                                              size="small"
                                              onClick={() => handleEditEnvironment(env)}
                                              sx={{ mr: 1 }}
                                            >
                                              <EditIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton
                                              edge="end"
                                              size="small"
                                              onClick={() => handleDeleteEnvironment(env.id)}
                                              color="error"
                                            >
                                              <DeleteIcon fontSize="small" />
                                            </IconButton>
                                          </ListItemSecondaryAction>
                                        </ListItem>
                                        {rebuildResult && (
                                          <Alert
                                            severity={rebuildResult.success ? 'success' : 'error'}
                                            sx={{ mt: 1, mb: 1 }}
                                            onClose={() => {
                                              const newResults = new Map(rebuildResults);
                                              newResults.delete(env.id);
                                              setRebuildResults(newResults);
                                            }}
                                          >
                                            {rebuildResult.message}
                                          </Alert>
                                        )}
                                      </Box>
                                    );
                                  })}
                                </List>
                              )}
                            </Box>
                          </AccordionDetails>
                        </Accordion>
                      );
                    })}
                  </Box>
                )}

                {/* Environment Form Dialog */}
                <Dialog open={showAddEnv || editingEnv !== null} onClose={() => {
                  setShowAddEnv(false);
                  setEditingEnv(null);
                  setEnvForm(emptyEnvForm);
                }}>
                  <DialogTitle>{editingEnv ? 'Edit Environment' : 'Add Environment'}</DialogTitle>
                  <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1, minWidth: 400 }}>
                      <TextField
                        fullWidth
                        label="Environment Name"
                        value={envForm.name}
                        onChange={(e) => setEnvForm({ ...envForm, name: e.target.value })}
                        placeholder="e.g., Production, Staging"
                        required
                      />
                      <TextField
                        fullWidth
                        label="S3 Prefix"
                        value={envForm.prefix}
                        onChange={(e) => setEnvForm({ ...envForm, prefix: e.target.value })}
                        placeholder="e.g., moodle1, prod/moodle"
                        required
                        helperText="The folder path in the S3 bucket"
                      />
                      <TextField
                        fullWidth
                        select
                        label="Bucket"
                        value={envForm.bucketId}
                        onChange={(e) => setEnvForm({ ...envForm, bucketId: e.target.value })}
                        required
                        SelectProps={{ native: true }}
                      >
                        <option value="">Select a bucket...</option>
                        {buckets.map((b) => (
                          <option key={b.id} value={b.id}>
                            {b.name}
                          </option>
                        ))}
                      </TextField>
                      <TextField
                        fullWidth
                        label="Description"
                        value={envForm.description}
                        onChange={(e) => setEnvForm({ ...envForm, description: e.target.value })}
                        placeholder="Optional description"
                        multiline
                        rows={2}
                      />
                    </Box>
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={() => {
                      setShowAddEnv(false);
                      setEditingEnv(null);
                      setEnvForm(emptyEnvForm);
                    }}>
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      onClick={editingEnv ? handleUpdateEnvironment : handleAddEnvironment}
                      disabled={!isEnvFormValid()}
                    >
                      {editingEnv ? 'Update' : 'Add'}
                    </Button>
                  </DialogActions>
                </Dialog>
              </Box>
            )}

            {/* ============================================================ */}
            {/* Tab 1: Moodle Sources */}
            {/* ============================================================ */}
            {activeTab === 1 && (
              <Box>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="body2" color="text.secondary">
                    Configure Kubernetes-based Moodle sources for direct content export.
                  </Typography>
                  <Box display="flex" gap={1}>
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={() => {
                        setEditingMoodleSourceId(undefined); // Clear editing mode for new source
                        setMoodleManagerOpen(true);
                      }}
                      size="small"
                    >
                      Add Source
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<RefreshIcon />}
                      onClick={loadData}
                      disabled={loading}
                      size="small"
                    >
                      Refresh
                    </Button>
                  </Box>
                </Box>

                {moodleSources.length === 0 ? (
                  <Alert severity="info">
                    No Moodle sources configured yet. Click "Add Source" to add one.
                  </Alert>
                ) : (
                  <Box>
                    {moodleSources.map((source) => {
                      const connectionResult = connectionResults.get(source.id);
                      const isTesting = testingConnection === source.id;

                      return (
                        <Accordion key={source.id}>
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Box display="flex" alignItems="center" gap={2} width="100%">
                              <SchoolIcon color="primary" />
                              <Typography variant="h6">{source.name}</Typography>
                              {connectionResult && (
                                <Chip
                                  size="small"
                                  icon={connectionResult.success ? <CheckCircleIcon /> : <ErrorIcon />}
                                  label={connectionResult.success ? 'Connected' : 'Failed'}
                                  color={connectionResult.success ? 'success' : 'error'}
                                />
                              )}
                            </Box>
                          </AccordionSummary>
                          <AccordionDetails>
                            <Box>
                              <Grid container spacing={1}>
                                <Grid item xs={12} sm={6}>
                                  <Typography variant="caption" color="text.secondary">
                                    Namespace
                                  </Typography>
                                  <Typography variant="body2">{source.namespace}</Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                  <Typography variant="caption" color="text.secondary">
                                    Pod Name
                                  </Typography>
                                  <Typography variant="body2">{source.podName}</Typography>
                                </Grid>
                                {source.description && (
                                  <Grid item xs={12}>
                                    <Typography variant="caption" color="text.secondary">
                                      Description
                                    </Typography>
                                    <Typography variant="body2">{source.description}</Typography>
                                  </Grid>
                                )}
                                <Grid item xs={12}>
                                  <Typography variant="caption" color="text.secondary">
                                    Added: {new Date(source.createdAt).toLocaleDateString()}
                                  </Typography>
                                </Grid>
                              </Grid>

                              {connectionResult && !connectionResult.success && (
                                <Alert severity="error" sx={{ mt: 2 }}>
                                  {connectionResult.message}
                                </Alert>
                              )}

                              <Box display="flex" gap={1} mt={2}>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={async () => {
                                    setTestingConnection(source.id);
                                    try {
                                      const result = await moodleApi.testConnection(source.id);
                                      setConnectionResults(new Map(connectionResults.set(source.id, result)));
                                    } catch (err) {
                                      setConnectionResults(
                                        new Map(
                                          connectionResults.set(source.id, {
                                            success: false,
                                            message: err instanceof Error ? err.message : 'Connection test failed',
                                          })
                                        )
                                      );
                                    } finally {
                                      setTestingConnection(null);
                                    }
                                  }}
                                  disabled={isTesting}
                                  startIcon={isTesting ? <CircularProgress size={16} /> : <CheckCircleIcon />}
                                >
                                  {isTesting ? 'Testing...' : 'Test Connection'}
                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<EditIcon />}
                                  onClick={() => {
                                    setEditingMoodleSourceId(source.id);
                                    setMoodleManagerOpen(true);
                                  }}
                                >
                                  Edit
                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="error"
                                  startIcon={<DeleteIcon />}
                                  onClick={async () => {
                                    if (!confirm('Are you sure you want to delete this Moodle source?')) {
                                      return;
                                    }
                                    try {
                                      await moodleApi.deleteSource(source.id);
                                      await loadData();
                                    } catch (err) {
                                      setError(err instanceof Error ? err.message : 'Failed to delete source');
                                    }
                                  }}
                                >
                                  Delete
                                </Button>
                              </Box>
                            </Box>
                          </AccordionDetails>
                        </Accordion>
                      );
                    })}
                  </Box>
                )}
              </Box>
            )}

            {/* ============================================================ */}
            {/* Tab 2: RangeOS Environments */}
            {/* ============================================================ */}
            {activeTab === 2 && (
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 3 }}>
                  Configure RangeOS environments for scenario management and CMI5 activity configuration.
                  These environments contain DevOps API URLs, Player URLs, and authentication settings.
                </Typography>

                <RangeOSEnvironmentManager />
              </Box>
            )}

            {/* ============================================================ */}
            {/* Tab 3: Integrations */}
            {/* ============================================================ */}
            {activeTab === 3 && (
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 3 }}>
                  Configure integrations with Rancher (Kubernetes cluster management), Vault (dynamic AWS credentials),
                  and manage kubeconfig files from multiple sources.
                </Typography>
                <IntegrationsManager />
              </Box>
            )}

            {/* ============================================================ */}
            {/* Tab 4: Export / Import */}
            {/* ============================================================ */}
            {activeTab === 4 && (
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 3 }}>
                  Export your complete configuration (S3 buckets, Moodle sources, RangeOS environments) to a file,
                  or import a configuration from a previously exported file. This is useful for backup, migration,
                  or sharing configurations between environments.
                </Typography>

                <Grid container spacing={3}>
                  {/* Export Section */}
                  <Grid item xs={12} md={6}>
                    <Card>
                      <CardContent>
                        <Box display="flex" alignItems="center" gap={1} mb={2}>
                          <DownloadIcon color="primary" />
                          <Typography variant="h6">Export Configuration</Typography>
                        </Box>

                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Download your current configuration as a file.
                        </Typography>

                        <Box mt={2}>
                          <Typography variant="subtitle2" gutterBottom>
                            Export Format
                          </Typography>
                          <Box display="flex" gap={1}>
                            <Button
                              variant={exportFormat === 'yaml' ? 'contained' : 'outlined'}
                              onClick={() => setExportFormat('yaml')}
                              size="small"
                            >
                              YAML
                            </Button>
                            <Button
                              variant={exportFormat === 'json' ? 'contained' : 'outlined'}
                              onClick={() => setExportFormat('json')}
                              size="small"
                            >
                              JSON
                            </Button>
                          </Box>
                        </Box>

                        <Box mt={2}>
                          <Button
                            variant="contained"
                            startIcon={isExporting ? <CircularProgress size={20} /> : <DownloadIcon />}
                            onClick={handleExportConfig}
                            disabled={isExporting}
                            fullWidth
                          >
                            {isExporting ? 'Exporting...' : 'Export Configuration'}
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Import Section */}
                  <Grid item xs={12} md={6}>
                    <Card>
                      <CardContent>
                        <Box display="flex" alignItems="center" gap={1} mb={2}>
                          <UploadIcon color="primary" />
                          <Typography variant="h6">Import Configuration</Typography>
                        </Box>

                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Upload a configuration file to import settings.
                        </Typography>

                        <Box mt={2}>
                          <input
                            accept=".yaml,.yml,.json"
                            style={{ display: 'none' }}
                            id="config-import-file"
                            type="file"
                            onChange={handleFileSelect}
                          />
                          <label htmlFor="config-import-file">
                            <Button variant="outlined" component="span" fullWidth>
                              {importFile ? importFile.name : 'Choose File'}
                            </Button>
                          </label>
                        </Box>

                        <Box mt={2}>
                          <Typography variant="subtitle2" gutterBottom>
                            Import Options
                          </Typography>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={importOptions.overwrite}
                                onChange={(e) => setImportOptions({ ...importOptions, overwrite: e.target.checked })}
                              />
                            }
                            label="Overwrite existing items"
                          />
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={importOptions.skipS3}
                                onChange={(e) => setImportOptions({ ...importOptions, skipS3: e.target.checked })}
                              />
                            }
                            label="Skip S3 buckets"
                          />
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={importOptions.skipMoodle}
                                onChange={(e) => setImportOptions({ ...importOptions, skipMoodle: e.target.checked })}
                              />
                            }
                            label="Skip Moodle sources"
                          />
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={importOptions.skipRangeOS}
                                onChange={(e) => setImportOptions({ ...importOptions, skipRangeOS: e.target.checked })}
                              />
                            }
                            label="Skip RangeOS environments"
                          />
                        </Box>

                        <Box mt={2}>
                          <Button
                            variant="contained"
                            startIcon={isImporting ? <CircularProgress size={20} /> : <UploadIcon />}
                            onClick={handleImportConfig}
                            disabled={!importFile || isImporting}
                            fullWidth
                          >
                            {isImporting ? 'Importing...' : 'Import Configuration'}
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Import Result */}
                  {importResult && (
                    <Grid item xs={12}>
                      <Alert
                        severity={importResult.success ? 'success' : 'error'}
                        onClose={() => setImportResult(null)}
                      >
                        <Typography variant="subtitle2" gutterBottom>
                          {importResult.message}
                        </Typography>
                        {importResult.success && importResult.summary && (
                          <Box mt={1}>
                            <Typography variant="body2">
                              S3 Buckets: {importResult.summary.s3Buckets.added} added, {importResult.summary.s3Buckets.updated} updated, {importResult.summary.s3Buckets.skipped} skipped
                            </Typography>
                            <Typography variant="body2">
                              S3 Environments: {importResult.summary.s3Environments.added} added, {importResult.summary.s3Environments.updated} updated, {importResult.summary.s3Environments.skipped} skipped
                            </Typography>
                            <Typography variant="body2">
                              Moodle Sources: {importResult.summary.moodleSources.added} added, {importResult.summary.moodleSources.updated} updated, {importResult.summary.moodleSources.skipped} skipped
                            </Typography>
                            <Typography variant="body2">
                              RangeOS Environments: {importResult.summary.rangeosEnvironments.added} added, {importResult.summary.rangeosEnvironments.updated} updated, {importResult.summary.rangeosEnvironments.skipped} skipped
                            </Typography>
                          </Box>
                        )}
                        {importResult.errors && importResult.errors.length > 0 && (
                          <Box mt={1}>
                            <Typography variant="body2" fontWeight="bold">Errors:</Typography>
                            {importResult.errors.map((err: string, idx: number) => (
                              <Typography key={idx} variant="body2">• {err}</Typography>
                            ))}
                          </Box>
                        )}
                      </Alert>
                    </Grid>
                  )}
                </Grid>
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>

      {/* Nested MoodleSourceManager for adding/editing sources */}
      <MoodleSourceManager
        open={moodleManagerOpen}
        editSourceId={editingMoodleSourceId}
        onClose={() => {
          setMoodleManagerOpen(false);
          setEditingMoodleSourceId(undefined); // Clear editing state
          loadData(); // Reload data after closing to refresh the list
        }}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog?.open || false}
        onClose={() => setDeleteDialog(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {deleteDialog?.type === 'bucket' ? 'Remove S3 Bucket?' : 'Remove Environment?'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Remove <strong>{deleteDialog?.name}</strong> from configuration?
          </Typography>
          
          {deleteDialog?.type === 'bucket' && (
            <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
              All environments associated with this bucket will also be removed from configuration.
            </Alert>
          )}

          <FormControlLabel
            control={
              <Checkbox
                checked={deleteDialog?.deleteContent || false}
                onChange={(e) => setDeleteDialog(prev => prev ? { ...prev, deleteContent: e.target.checked } : null)}
                color="error"
              />
            }
            label={
              <Box>
                <Typography variant="body2" color="error" fontWeight="bold">
                  Also delete all files from S3 (DANGER!)
                </Typography>
                {deleteDialog?.type === 'environment' && (
                  <Typography variant="caption" color="text.secondary">
                    This includes courses, categories, competency frameworks, and learning path templates
                  </Typography>
                )}
              </Box>
            }
          />

          {deleteDialog?.deleteContent && (
            <Alert severity="error" sx={{ mt: 2 }}>
              <Typography variant="body2" fontWeight="bold">
                ⚠️ WARNING: This action cannot be undone!
              </Typography>
              <Typography variant="caption">
                All files will be permanently deleted from S3.
              </Typography>
            </Alert>
          )}

          {!deleteDialog?.deleteContent && (
            <Alert severity="success" sx={{ mt: 2 }}>
              <Typography variant="body2">
                Files will remain safe in S3. Only configuration will be removed.
              </Typography>
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(null)}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
          >
            {deleteDialog?.deleteContent ? 'Delete Configuration & Files' : 'Remove Configuration'}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};
